import type { Farm, ProductionUnit } from '../types/farm'
import type { Lot, LotEvent, LotEventHistoryScope, LotLineageProjectionRow } from '../types/lot'
import type { FeedLog, FishStockLog, HarvestLog, LotCostEvent, MortalityLog, ProductionRecord, WaterLog, WeightSampleLog } from '../types/production'
import type { FxRateLog } from '../types/fx'
import type { InventoryItem } from '../types/inventory'
import type {
  ExternalSource,
  ExternalSourceLink,
  ExternalSourceLinkFilters,
  ExternalSourceLinkUpdatePayload
} from '../types/externalIdentifier'
import type { MortalityMatrixRecord } from '../types/mortalityMatrix'
import type { MatrixPayload, SfrMatrixRecord } from '../types/sfrMatrix'
import type {
  LotStoryArtifactBundleV1,
  LotStoryArtifactV1,
  LotStoryArtifactDetailV1,
  LotStoryArtifactListRequest,
  LotStoryActorContextV1,
  LotStoryArtifactStatus,
  LotStoryArtifactVersionV1,
  LotStoryArtifactVisibility,
  LotStoryDraftCreateRequest,
  LotStoryPreviewRequest,
  LotStoryProjectionReadinessStatus,
  LotStoryProjectionV1
} from '../types/lotStory'
import type {
  TaskCreateRequest,
  TaskRecord,
  TaskUpdateRequest
} from '../types/task'

import { getActiveTenantKey, setActiveTenantKey } from '../offline/tenant'
import { clearStoredApiToken, getStoredApiToken, getStoredPrincipal } from './apiAuth'

type RequestOptions = RequestInit & {
  query?: Record<string, string | undefined>
}

type ListOptions = {
  noCache?: boolean
}

type ApiEnvelope<T> = {
  data: T
  error?: never
} | {
  data?: never
  error: string
}

/** The API's machine-readable error code (`{ error, code }` bodies), if any. */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined
  const code = (error.body as { code?: unknown } | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

export class ApiError extends Error {
  readonly status: number
  readonly method?: string
  readonly url?: string
  readonly body?: unknown

  constructor(message: string, status: number, details?: { method?: string; url?: string; body?: unknown }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.method = details?.method
    this.url = details?.url
    this.body = details?.body
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api'
const API_TOKEN_ENV = (import.meta.env.VITE_API_TOKEN as string | undefined)?.trim()

const getApiToken = (): string | undefined => {
  const stored = getStoredApiToken()
  if (stored) return stored
  return API_TOKEN_ENV
}
  
if (import.meta.env.DEV) {
  // Surface env wiring issues during development.
  console.info('[api] config', { base: API_BASE_URL, hasToken: Boolean(API_TOKEN_ENV) })
  console.debug('[api] token preview', {
    head: API_TOKEN_ENV?.slice(0, 8),
    tail: API_TOKEN_ENV?.slice(-8)
  })
}

const buildUrl = (path: string, query?: Record<string, string | undefined>) => {
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const searchParams = new URLSearchParams()

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value) searchParams.set(key, value)
  })

  const queryString = searchParams.toString()
  const suffix = queryString ? `?${queryString}` : ''

  if (/^https?:/i.test(base)) {
    const url = new URL(`${base}${normalizedPath}`)
    if (queryString) url.search = queryString
    return url.toString()
  }

  return `${base}${normalizedPath}${suffix}`
}

const normalizePath = (value: string): string => encodeURIComponent(value)

const handleUnauthorizedResponse = (): void => {
  clearStoredApiToken()
  setActiveTenantKey(null)
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, headers, ...rest } = options
  const isJson = rest.body !== undefined && !(rest.body instanceof FormData)
  const finalHeaders = new Headers(headers)
  if (isJson && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json')
  }

  if (rest.cache === 'no-store') {
    // Help ensure a manual refresh actually re-fetches bytes even when ETags are stable.
    if (!finalHeaders.has('Cache-Control')) {
      finalHeaders.set('Cache-Control', 'no-cache')
    }
    if (!finalHeaders.has('Pragma')) {
      finalHeaders.set('Pragma', 'no-cache')
    }
  }

  const apiToken = getApiToken()
  if (apiToken && !finalHeaders.has('Authorization')) {
    finalHeaders.set('Authorization', `Bearer ${apiToken}`)
  }

  const storedPrincipal = getStoredPrincipal()
  if (storedPrincipal?.actorId && !finalHeaders.has('x-actor-id')) {
    finalHeaders.set('x-actor-id', storedPrincipal.actorId)
  }

  const prefersCanonicalMembershipIdentity = Boolean(
    apiToken?.startsWith('sun_')
    && storedPrincipal?.tenantMembershipId
  )
  const shouldForwardLegacyTenantMemberId = Boolean(
    storedPrincipal?.tenantMemberId
    && !prefersCanonicalMembershipIdentity
  )
  if (shouldForwardLegacyTenantMemberId && !finalHeaders.has('x-tenant-member-id')) {
    const tenantMemberId = storedPrincipal?.tenantMemberId
    if (tenantMemberId) {
      finalHeaders.set('x-tenant-member-id', tenantMemberId)
    }
  }

  if (storedPrincipal?.userId && !finalHeaders.has('x-user-id')) {
    finalHeaders.set('x-user-id', storedPrincipal.userId)
  }

  if (storedPrincipal?.tenantMembershipId && !finalHeaders.has('x-tenant-membership-id')) {
    finalHeaders.set('x-tenant-membership-id', storedPrincipal.tenantMembershipId)
  }

  if (!finalHeaders.has('x-tenant-key')) {
    finalHeaders.set('x-tenant-key', getActiveTenantKey())
  }

  const url = buildUrl(path, query)
  const method = (rest.method ?? 'GET').toUpperCase()

  let response: Response
  try {
    response = await fetch(url, {
      ...rest,
      headers: finalHeaders
    })
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[api] network error', {
        method,
        url,
        message: error instanceof Error ? error.message : String(error)
      })
    }
    throw new ApiError('Network error', 0, { method, url })
  }

  const raw = await response.text()
  const trimmed = raw.trim()

  if (trimmed.length === 0) {
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorizedResponse()
      }
      if (import.meta.env.DEV) {
        console.warn('[api] empty response', { method, url, status: response.status, statusText: response.statusText })
      }
      throw new ApiError(response.statusText || 'Request failed', response.status, { method, url })
    }
    return undefined as T
  }

  let payload: (ApiEnvelope<T> & Record<string, unknown>)
  try {
    payload = JSON.parse(trimmed) as (ApiEnvelope<T> & Record<string, unknown>)
  } catch {
    throw new Error('Invalid JSON response')
  }

  if (!response.ok || 'error' in payload) {
    const message = 'error' in payload && typeof payload.error === 'string' ? payload.error : response.statusText
    if (response.status === 401) {
      handleUnauthorizedResponse()
    }
    if (import.meta.env.DEV) {
      console.warn('[api] request failed', { method, url, status: response.status, message: message || response.statusText })
    }
    throw new ApiError(message || 'Request failed', response.status, { method, url, body: payload })
  }

  return payload.data
}

export type TaskCreatePayload = TaskCreateRequest
export type TaskUpdatePayload = TaskUpdateRequest
export type SfrMatrixPayload = MatrixPayload

export type FarmCreatePayload = Omit<Farm, 'createdAt' | 'updatedAt' | 'productionUnits'> & {
  id?: string
}
export type FarmUpdatePayload = Partial<Omit<Farm, 'id' | 'createdAt' | 'updatedAt' | 'productionUnits'>>

export type ProductionUnitCreatePayload = Omit<ProductionUnit, 'createdAt' | 'updatedAt'> & {
  id?: string
}
export type ProductionUnitUpdatePayload = Partial<Omit<ProductionUnit, 'id' | 'createdAt' | 'updatedAt' | 'farmId'>>

export type LotCreatePayload = Omit<Lot, 'createdAt' | 'updatedAt'> & {
  id?: string
  eventId?: string
}
export type LotUpdatePayload = Partial<Omit<Lot, 'id'>> & {
  eventId?: string
}
export type QueuedLotUpsertPayload = Omit<LotCreatePayload, 'eventId'> & {
  localLedgerEvent?: LotEvent
}
export type LotMoveCommandPayload = {
  targetProductionUnitId?: string
  targetLocationType?: 'production-unit' | 'processing-site'
  targetLocationId?: string
  eventId?: string
  recordedAt: string
  notes?: string
}

// Findings #183: part (or all) of a lot's fish join the same-name lot in another unit.
export type LotMoveFishCommandPayload = {
  targetLotId: string
  fishMoved: number
  eventId?: string
  recordedAt: string
  notes?: string
}

export type QueuedLotMoveCommandPayload = LotMoveCommandPayload & {
  sourceLotId: string
  eventId: string
}

export type LotTransferLocationType = 'production-unit' | 'farm' | 'processing-site'

export type LotTransferCommandPayload = {
  targetFarmId: string
  targetProductionUnitId?: string
  targetLocationType?: LotTransferLocationType
  targetLocationId?: string
  eventId?: string
  recordedAt: string
  notes?: string
}

export type QueuedLotTransferCommandPayload = LotTransferCommandPayload & {
  sourceLotId: string
  eventId: string
}

export type LotSplitCommandPayload = {
  childLotKeys: string[]
  childLotIds?: string[]
  eventId?: string
  recordedAt: string
  notes?: string
}

export type QueuedLotSplitCommandPayload = LotSplitCommandPayload & {
  sourceLotId: string
  childLotIds: string[]
  eventId: string
}

export type LotMergeCommandPayload = {
  sourceLotIds: string[]
  targetLotKey: string
  targetLotId?: string
  eventId?: string
  recordedAt: string
  notes?: string
  markSourceLotsInactive?: boolean
}

export type QueuedLotMergeCommandPayload = LotMergeCommandPayload & {
  targetLotId: string
  eventId: string
}

export type LotSplitCommandResult = {
  createdLots: Lot[]
  updatedSourceLot: Lot
  events: LotEvent[]
}

export type LotMoveFishCommandResult = {
  sourceLot: Lot
  targetLot: Lot
  events: LotEvent[]
}

export type LotMoveCommandResult = {
  movedLot: Lot
  events: LotEvent[]
}

export type LotTransferCommandResult = {
  transferredLot: Lot
  events: LotEvent[]
}

export type LotMergeCommandResult = {
  targetLot: Lot
  updatedSourceLots: Lot[]
  events: LotEvent[]
}

export type MeResponse = {
  tenantKey: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  actorId: string | null
  tenantMemberId: string | null
  currentTokenId: string | null
  userId: string | null
  tenantMembershipId: string | null
  accountDisplayName: string | null
  email: string | null
  username: string | null
}

export type AdminApiTokenRecord = {
  id: string
  tenantKey: string
  actorId: string
  userId: string | null
  tenantMembershipId: string | null
  label: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  revokedAt: string | null
  lastUsedAt: string | null
  createdAt: string
  updatedAt: string
}

export type AdminApiTokenCreateRequest = {
  label: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  actorId?: string
}

export type TeamMemberRecord = {
  id: string
  tenantKey: string
  actorId: string
  userId: string | null
  tenantMembershipId: string | null
  credentialStatus: 'activationRequired' | 'credentialsReady'
  /** Findings #121: optional — older API builds don't send them. */
  hasPin?: boolean
  hasSignIn?: boolean
  /** Findings #169: optional — older API builds don't send it. */
  neverSignedIn?: boolean
  legacyTokenIds: string[]
  label: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  status: 'active' | 'revoked'
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  activeTokenCount: number
  revokedTokenCount: number
}

export type TeamMemberUpdateRequest = {
  role: 'viewer' | 'contributor' | 'editor'
}

export type TeamMemberRevokeResponse = {
  id: string
  tenantMembershipId: string
  revokedAt: string
  revokedTokenCount: number
}

export type TeamMemberListOptions = {
  includeRevoked?: boolean
}

// Crew Mode Charter D-4 shared identity mechanism.
export type OperatorRosterEntry = {
  tenantMembershipId: string
  actorId: string
  displayName: string
  pinHash: string
  pinUpdatedAt: string
}

export type OperatorPinSetResponse = {
  tenantMembershipId: string
  pinUpdatedAt: string
}

export type TeamMemberCreateRequest = {
  displayName: string
  role: 'viewer' | 'contributor' | 'editor'
  actorId?: string
  email?: string
  username?: string
  password?: string
}

export type TeamMemberCreateResponse = {
  token: string
  member: {
    id: string
    tenantKey: string
    actorId: string
    userId: string
    tenantMembershipId: string
    label: string
    role: 'viewer' | 'contributor' | 'editor' | 'admin'
    email?: string | null
    username?: string | null
    credentialStatus: 'activationRequired' | 'credentialsReady'
    legacyTokenId: string
    createdAt: string
    lastUsedAt: string | null
  }
}

export type CredentialResetPurpose = 'activation' | 'passwordReset'

export type TeamMemberPasswordResetIssueResponse = {
  token: string
  expiresAt: string
  purpose: CredentialResetPurpose
}

export type CredentialSignInRequest = {
  identifier: string
  password: string
  tenantKey?: string
}

export type CredentialSignInResponse = {
  token: string
  principal: MeResponse
  user: {
    id: string
    displayName: string
    email: string | null
    username: string | null
  }
}

export type PasswordResetPreviewRequest = {
  token: string
}

export type PasswordResetPreviewResponse = {
  purpose: CredentialResetPurpose
  tenantKey: string
  tenantMembershipId: string
  displayName: string
  accountLabel: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  email: string | null
  username: string | null
  expiresAt: string
}

export type PasswordResetCompleteRequest = {
  token: string
  email: string
  username: string
  password: string
}

export type PasswordResetCompleteResponse = {
  tenantKey: string
  tenantMembershipId: string
  userId: string
  actorId: string
  email: string | null
  username: string | null
  purpose: CredentialResetPurpose
}

export type CommunityGroupMembershipRecord = {
  id: string
  groupId: string
  userId: string
  tenantMembershipId: string
  partnerOrganizationId: string | null
  role: 'owner' | 'editor' | 'viewer'
  status: 'invited' | 'active' | 'revoked'
  tenantKey: string
  label: string
  actorId: string
  displayName: string
  partnerOrganizationName: string | null
  createdAt: string
  updatedAt: string
}

export type CommunityGroupRecord = {
  id: string
  ownerTenantKey: string
  displayName: string
  status: 'active' | 'archived'
  createdByUserId: string
  createdByActorId: string
  createdByDisplayName: string
  createdAt: string
  updatedAt: string
  activeMembershipCount: number
  invitedMembershipCount: number
  revokedMembershipCount: number
  memberships: CommunityGroupMembershipRecord[]
}

export type CommunityPartnerOrganizationRecord = {
  id: string
  displayName: string
  ownerTenantKey: string | null
  status: 'active' | 'inactive'
  createdAt: string
  updatedAt: string
}

export type CommunityGroupCreateRequest = {
  displayName: string
}

export type CommunityPartnerOrganizationCreateRequest = {
  displayName: string
}

export type CommunityProfileRecord = {
  tenantKey: string
  actorId: string
  accountDisplayName: string
  tenantMembershipId: string
  membershipRole: 'viewer' | 'contributor' | 'editor' | 'admin'
  userId: string
  communityProfileId: string | null
  displayName: string | null
  publicBio: string | null
  createdAt: string | null
  updatedAt: string | null
}

export type CommunityProfileUpdateRequest = {
  displayName?: string
  publicBio?: string
}

export type CommunityActorContextGroupRecord = {
  userId: string
  tenantKey: string
  tenantMembershipId: string
  groupId: string
  groupMembershipId: string
  partnerOrganizationId: string | null
  groupDisplayName: string
  ownerTenantKey: string
  groupStatus: 'active' | 'archived'
  groupRole: 'owner' | 'editor' | 'viewer'
  membershipStatus: 'invited' | 'active' | 'revoked'
  partnerOrganizationName: string | null
  createdAt: string
  updatedAt: string
}

export type CommunityActorContextRecord = {
  userId: string
  tenantKey: string
  tenantMembershipId: string
  membershipRole: 'viewer' | 'contributor' | 'editor' | 'admin'
  actorId: string
  accountDisplayName: string
  communityProfileId: string | null
  communityDisplayName: string | null
  publicBio: string | null
  tenantActorContext: {
    userId: string
    tenantKey: string
    tenantMembershipId: string
  }
  activeGroupContextCount: number
  invitedGroupContextCount: number
  revokedGroupContextCount: number
  groupActorContexts: CommunityActorContextGroupRecord[]
}

export type LotStoryGroupReadPreflightBlockingReason =
  | 'artifact-not-published'
  | 'artifact-not-group-visible'
  | 'no-active-group-context'
  | 'no-target-group-selected'
  | 'target-group-not-eligible'
  | 'target-group-not-saved'

export type LotStoryGroupReadPreflightRecord = {
  artifactId: string
  ownerTenantKey: string
  artifactStatus: LotStoryArtifactStatus
  artifactVisibility: LotStoryArtifactVisibility
  currentDraftVersionId: string | null
  currentPublishedVersionId: string | null
  actorContext: Pick<CommunityActorContextRecord,
    'userId' |
    'tenantKey' |
    'tenantMembershipId' |
    'membershipRole' |
    'actorId' |
    'accountDisplayName' |
    'communityDisplayName' |
    'activeGroupContextCount'
  >
  savedGroupDisplayName: string | null
  selectedGroupId: string | null
  selectedGroupDisplayName: string | null
  selectedGroupEligible: boolean | null
  selectedGroupContext: CommunityActorContextGroupRecord | null
  eligibleGroupContextCount: number
  eligibleGroupActorContexts: CommunityActorContextGroupRecord[]
  groupReadAllowed: boolean
  blockingReasons: LotStoryGroupReadPreflightBlockingReason[]
}

export type LotStoryGroupDeliveryRecord = {
  artifact: Pick<LotStoryArtifactV1,
    'artifactId'
    | 'artifactType'
    | 'tenantKey'
    | 'lineageRootId'
    | 'status'
    | 'visibility'
    | 'groupId'
    | 'currentPublishedVersionId'
    | 'createdAt'
    | 'updatedAt'
  >
  version: Pick<LotStoryArtifactVersionV1,
    'artifactVersionId'
    | 'artifactId'
    | 'tenantKey'
    | 'versionNumber'
    | 'versionStatus'
    | 'visibility'
    | 'title'
    | 'projectionSnapshot'
    | 'projectionGeneratedAt'
    | 'readinessStatus'
    | 'createdAt'
    | 'updatedAt'
  > & {
    summary: string | null
    notes: string | null
    publishedAt: string | null
    publishedByContext: LotStoryActorContextV1 | null
  }
  deliveryContext: {
    ownerTenantKey: string
    receiverActorContext: Pick<CommunityActorContextRecord,
      'userId'
      | 'tenantKey'
      | 'tenantMembershipId'
      | 'membershipRole'
      | 'actorId'
      | 'accountDisplayName'
      | 'communityDisplayName'
      | 'activeGroupContextCount'
    >
    grantedGroupContext: CommunityActorContextGroupRecord
  }
}

export type LotStoryGroupDeliveryReceiptRecord = {
  artifactId: string
  ownerTenantKey: string
  grantedGroupId: string | null
  currentPublishedVersionId: string | null
  latestReceipt: {
    viewedAt: string
    viewerContext: {
      userId: string | null
      tenantMembershipId: string | null
      actorId: string | null
      accountDisplayName: string | null
      communityDisplayName: string | null
    }
    grantedGroupId: string | null
    grantedGroupRole: string | null
    currentPublishedVersionId: string | null
  } | null
  receiptHistory: Array<{
    viewedAt: string
    viewerContext: {
      userId: string | null
      tenantMembershipId: string | null
      actorId: string | null
      accountDisplayName: string | null
      communityDisplayName: string | null
    }
    grantedGroupId: string | null
    grantedGroupRole: string | null
    currentPublishedVersionId: string | null
  }>
  hasMoreReceiptHistory: boolean
  nextReceiptHistoryCursor: string | null
}

export type LotStoryGroupDeliveryReceiptRequest = {
  historyCursor?: string
  historyLimit?: number
}

export type LotStoryGroupDeliveryListEntry = {
  artifact: Pick<LotStoryArtifactV1,
    'artifactId'
    | 'artifactType'
    | 'tenantKey'
    | 'lineageRootId'
    | 'status'
    | 'visibility'
    | 'groupId'
    | 'currentPublishedVersionId'
    | 'createdAt'
    | 'updatedAt'
  >
  version: Pick<LotStoryArtifactVersionV1,
    'artifactVersionId'
    | 'artifactId'
    | 'tenantKey'
    | 'versionNumber'
    | 'versionStatus'
    | 'visibility'
    | 'title'
    | 'projectionGeneratedAt'
    | 'readinessStatus'
    | 'createdAt'
    | 'updatedAt'
  > & {
    summary: string | null
    publishedAt: string | null
    publishedByContext: LotStoryActorContextV1 | null
  }
  deliveryContext: {
    ownerTenantKey: string
    receiverActorContext: Pick<CommunityActorContextRecord,
      'userId'
      | 'tenantKey'
      | 'tenantMembershipId'
      | 'membershipRole'
      | 'actorId'
      | 'accountDisplayName'
      | 'communityDisplayName'
      | 'activeGroupContextCount'
    >
    grantedGroupContext: CommunityActorContextGroupRecord
  }
}

export type LotStoryGroupDeliveryListRecord = {
  actorContext: Pick<CommunityActorContextRecord,
    'userId'
    | 'tenantKey'
    | 'tenantMembershipId'
    | 'membershipRole'
    | 'actorId'
    | 'accountDisplayName'
    | 'communityDisplayName'
    | 'activeGroupContextCount'
  >
  selectedGroupId: string | null
  selectedGroupContext: CommunityActorContextGroupRecord | null
  activeGroupActorContexts: CommunityActorContextGroupRecord[]
  deliveries: LotStoryGroupDeliveryListEntry[]
}

export type LotStoryGroupShareReviewPreflightBlockingReason =
  | 'artifact-not-published'
  | 'artifact-readiness-blocked'
  | 'no-active-group-context'
  | 'no-target-group-selected'
  | 'target-group-not-eligible'
  | 'target-group-not-saved'

export type LotStoryGroupShareApprovalPreflightBlockingReason =
  | LotStoryGroupShareReviewPreflightBlockingReason
  | 'review-not-recorded'

export type LotStoryGroupShareReleaseGatePreflightBlockingReason =
  | LotStoryGroupShareApprovalPreflightBlockingReason
  | 'approval-not-recorded'

export type LotStoryGroupSharePromotionPreflightBlockingReason =
  | LotStoryGroupShareReleaseGatePreflightBlockingReason
  | 'release-gate-not-recorded'
  | 'artifact-already-group-visible'

export type LotStoryGroupShareReviewPreflightRecord = {
  artifactId: string
  ownerTenantKey: string
  artifactStatus: LotStoryArtifactStatus
  artifactVisibility: LotStoryArtifactVisibility
  artifactReadinessStatus: LotStoryProjectionReadinessStatus | null
  currentPublishedVersionId: string | null
  actorContext: Pick<CommunityActorContextRecord,
    'userId' |
    'tenantKey' |
    'tenantMembershipId' |
    'membershipRole' |
    'actorId' |
    'accountDisplayName' |
    'communityDisplayName' |
    'activeGroupContextCount'
  >
  savedGroupId: string | null
  savedGroupDisplayName: string | null
  selectedGroupId: string | null
  selectedGroupDisplayName: string | null
  selectedGroupEligible: boolean | null
  selectedGroupMatchesSavedIntent: boolean
  selectedGroupContext: CommunityActorContextGroupRecord | null
  reviewedGroupId: string | null
  reviewedPublishedVersionId: string | null
  reviewRecorded: boolean
  reviewRecordedAt: string | null
  reviewRecordedByContext: LotStoryActorContextV1 | null
  approvedGroupId: string | null
  approvedPublishedVersionId: string | null
  approvalRecorded: boolean
  approvalRecordedAt: string | null
  approvalRecordedByContext: LotStoryActorContextV1 | null
  releaseGateGroupId: string | null
  releaseGatePublishedVersionId: string | null
  releaseGateRecorded: boolean
  releaseGateRecordedAt: string | null
  releaseGateRecordedByContext: LotStoryActorContextV1 | null
  promotedGroupId: string | null
  promotedPublishedVersionId: string | null
  promotionRecorded: boolean
  promotedAt: string | null
  promotedByContext: LotStoryActorContextV1 | null
  eligibleGroupContextCount: number
  eligibleGroupActorContexts: CommunityActorContextGroupRecord[]
  groupShareReviewReady: boolean
  blockingReasons: LotStoryGroupShareReviewPreflightBlockingReason[]
  groupShareApprovalReady: boolean
  approvalBlockingReasons: LotStoryGroupShareApprovalPreflightBlockingReason[]
  groupShareReleaseGateReady: boolean
  releaseGateBlockingReasons: LotStoryGroupShareReleaseGatePreflightBlockingReason[]
  groupSharePromotionReady: boolean
  promotionBlockingReasons: LotStoryGroupSharePromotionPreflightBlockingReason[]
}

export type LotStoryGroupShareIntentUpdateRequest = {
  groupId?: string | null
}

export type CommunityGroupMembershipCreateRequest = {
  tenantMembershipId: string
  role: 'owner' | 'editor' | 'viewer'
  membershipStatus?: 'invited' | 'active' | 'revoked'
  partnerOrganizationId?: string
}

type SfrMatrixSavePayload = {
  species: string
  matrix: MatrixPayload
}

type MortalityMatrixSavePayload = {
  species: string
  matrix: MatrixPayload
}

export type TreatmentMethod = 'bath' | 'feed' | 'injection' | 'other'

export type TreatmentCreatePayload = {
  id: string
  farmId: string
  lotId: string
  productionUnitIds: string[]
  appliedAt: string
  method: TreatmentMethod
  productName: string
  inventoryItemId?: string
  dose?: string
  quantityUsed?: number
  quantityUnit?: string
  durationMinutes?: number
  reason: string
  prescribedBy?: string
  withdrawalDays: number
  notes?: string
  costEventId?: string
}

export type TreatmentRecord = Omit<TreatmentCreatePayload, 'inventoryItemId' | 'dose' | 'quantityUsed' | 'quantityUnit' | 'durationMinutes' | 'prescribedBy' | 'notes' | 'costEventId'> & {
  inventoryItemId: string | null
  dose: string | null
  quantityUsed: number | null
  quantityUnit: string | null
  durationMinutes: number | null
  prescribedBy: string | null
  notes: string | null
  costEventId: string | null
  withdrawalEndsAt: string | null
  recordedByActorId: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  deletedReason: string | null
}

export const api = {
  auth: {
    signIn: (payload: CredentialSignInRequest) =>
      request<CredentialSignInResponse>('/auth/sign-in', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    previewPasswordReset: (payload: PasswordResetPreviewRequest) =>
      request<PasswordResetPreviewResponse>('/auth/password-reset/preview', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    resetPassword: (payload: PasswordResetCompleteRequest) =>
      request<PasswordResetCompleteResponse>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
  },
  me: {
    get: () => request<MeResponse>('/me')
  },
  adminApiTokens: {
    list: () => request<AdminApiTokenRecord[]>('/admin/api-tokens'),
    create: (payload: AdminApiTokenCreateRequest) =>
      request<{ token: string; apiToken: AdminApiTokenRecord }>('/admin/api-tokens', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    revoke: (id: string) => request<{ id: string; revokedAt: string | null }>(`/admin/api-tokens/${normalizePath(id)}/revoke`, {
      method: 'POST'
    })
  },
  teamMembers: {
    list: (options?: TeamMemberListOptions) => request<TeamMemberRecord[]>('/team/members', {
      query: {
        includeRevoked: options?.includeRevoked ? 'true' : undefined
      }
    }),
    create: (payload: TeamMemberCreateRequest) =>
      request<TeamMemberCreateResponse>('/team/members', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    issuePasswordReset: (id: string) =>
      request<TeamMemberPasswordResetIssueResponse>(`/team/members/${normalizePath(id)}/password-reset`, {
        method: 'POST'
      }),
    update: (id: string, payload: TeamMemberUpdateRequest) =>
      request<TeamMemberRecord>(`/team/members/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    revoke: (id: string) =>
      request<TeamMemberRevokeResponse>(`/team/members/${normalizePath(id)}/revoke`, {
        method: 'POST'
      }),
    operatorRoster: () => request<OperatorRosterEntry[]>('/team/members/operator-roster'),
    setPin: (id: string, pin: string) =>
      request<OperatorPinSetResponse>(`/team/members/${normalizePath(id)}/pin`, {
        method: 'POST',
        body: JSON.stringify({ pin })
      }),
    clearPin: (id: string) =>
      request<{ tenantMembershipId: string }>(`/team/members/${normalizePath(id)}/pin/clear`, {
        method: 'POST'
      })
  },
  communityGroups: {
    list: () => request<CommunityGroupRecord[]>('/community/groups'),
    create: (payload: CommunityGroupCreateRequest) =>
      request<CommunityGroupRecord>('/community/groups', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    addMembership: (groupId: string, payload: CommunityGroupMembershipCreateRequest) =>
      request<CommunityGroupMembershipRecord>(`/community/groups/${normalizePath(groupId)}/memberships`, {
        method: 'POST',
        body: JSON.stringify(payload)
      })
  },
  communityPartnerOrganizations: {
    list: () => request<CommunityPartnerOrganizationRecord[]>('/community/partner-organizations'),
    create: (payload: CommunityPartnerOrganizationCreateRequest) =>
      request<CommunityPartnerOrganizationRecord>('/community/partner-organizations', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
  },
  communityProfile: {
    get: () => request<CommunityProfileRecord>('/community/profile'),
    update: (payload: CommunityProfileUpdateRequest) =>
      request<CommunityProfileRecord>('/community/profile', {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
  },
  communityActorContext: {
    get: () => request<CommunityActorContextRecord>('/community/actor-context')
  },
  farms: {
    list: (options?: ListOptions) => request<Farm[]>('/farms', {
      cache: options?.noCache ? 'no-store' : undefined
    }),
    get: (id: string) => request<Farm>(`/farms/${normalizePath(id)}`),
    create: (payload: FarmCreatePayload) =>
      request<Farm>('/farms', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: FarmUpdatePayload) =>
      request<Farm>(`/farms/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/farms/${normalizePath(id)}`, { method: 'DELETE' }),
    holdings: (id: string) =>
      request<RecordHoldings>(`/farms/${normalizePath(id)}/holdings`, { cache: 'no-store' })
  },
  productionUnits: {
    list: (params?: { farmId?: string; noCache?: boolean }) => request<ProductionUnit[]>('/production-units', {
      query: { farmId: params?.farmId },
      cache: params?.noCache ? 'no-store' : undefined
    }),
    get: (id: string) => request<ProductionUnit>(`/production-units/${normalizePath(id)}`),
    create: (payload: ProductionUnitCreatePayload) =>
      request<ProductionUnit>('/production-units', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: ProductionUnitUpdatePayload) =>
      request<ProductionUnit>(`/production-units/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/production-units/${normalizePath(id)}`, { method: 'DELETE' }),
    holdings: (id: string) =>
      request<RecordHoldings>(`/production-units/${normalizePath(id)}/holdings`, { cache: 'no-store' })
  },
  productionUnitAliases: {
    list: (params?: { status?: 'resolved' | 'unresolved' | 'all' }) =>
      request<ProductionUnitAlias[]>('/production-unit-aliases', {
        query: { status: params?.status }
      }),
    register: (payload: ProductionUnitAliasRegisterPayload) =>
      request<ProductionUnitAlias>('/production-unit-aliases', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    resolve: (aliasKey: string, payload: ProductionUnitAliasResolvePayload) =>
      request<ProductionUnitAlias>(`/production-unit-aliases/${encodeURIComponent(aliasKey)}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
  },
  lots: {
    list: (params?: { farmId?: string; productionUnitId?: string; lotKey?: string; stage?: string; noCache?: boolean }) =>
      request<Lot[]>('/lots', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lotKey: params?.lotKey,
          stage: params?.stage
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    listEvents: (id: string, params?: { scope?: LotEventHistoryScope }) =>
      request<LotEvent[]>(`/lots/${normalizePath(id)}/events`, {
        query: {
          scope: params?.scope
        }
      }),
    lineage: (farmId: string) =>
      request<LotLineageProjectionRow[]>('/lots/lineage', {
        query: { farmId }
      }),
    // Findings #133/#134: deleted lots of a farm (id + name), to label records
    // that still point at them.
    // Findings #205: who deleted it and, with `includeRestored`, lots restored since.
    deleted: (farmId: string, options: { includeRestored?: boolean } = {}) =>
      request<Array<{
        id: string
        lotKey: string
        deletedAt: string
        productionUnitId?: string
        createdAt?: string
        deletedByActorId?: string
        restoredAt?: string
        restoredByActorId?: string
      }>>('/lots/deleted', {
        query: { farmId, ...(options.includeRestored ? { includeRestored: '1' } : {}) }
      }),
    // Findings #197: Lotes → Eliminados → Restaurar (online only).
    restore: (id: string) =>
      request<Lot>(`/lots/${normalizePath(id)}/restore`, { method: 'POST' }),
    lineageForLots: (lotIds: readonly string[]) =>
      request<LotLineageProjectionRow[]>('/lots/lineage', {
        query: { lotIds: lotIds.join(',') }
      }),
    create: (payload: LotCreatePayload) =>
      request<Lot>('/lots', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    move: (id: string, payload: LotMoveCommandPayload) =>
      request<LotMoveCommandResult>(`/lots/${normalizePath(id)}/move`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    moveFish: (id: string, payload: LotMoveFishCommandPayload) =>
      request<LotMoveFishCommandResult>(`/lots/${normalizePath(id)}/move-fish`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    transfer: (id: string, payload: LotTransferCommandPayload) =>
      request<LotTransferCommandResult>(`/lots/${normalizePath(id)}/transfer`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    merge: (payload: LotMergeCommandPayload) =>
      request<LotMergeCommandResult>('/lots/merge', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    split: (id: string, payload: LotSplitCommandPayload) =>
      request<LotSplitCommandResult>(`/lots/${normalizePath(id)}/split`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    // findings #67 / #71: "Sembrar en varias unidades" (one batch, one lot per unit).
    stockIntoUnits: (payload: LotStockIntoUnitsPayload) =>
      request<LotStockIntoUnitsResult>('/lots/stock-into-units', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: LotUpdatePayload) =>
      request<Lot>(`/lots/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) =>
      request<void>(`/lots/${id}`, { method: 'DELETE' })
  },
  lotStories: {
    list: (params?: LotStoryArtifactListRequest) =>
      request<LotStoryArtifactBundleV1[]>('/community/artifacts/lot-stories', {
        query: {
          lineageRootId: params?.lineageRootId
        }
      }),
    get: (artifactId: string) =>
      request<LotStoryArtifactDetailV1>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}`),
    listGroupDeliveries: (groupId?: string) =>
      request<LotStoryGroupDeliveryListRecord>('/community/artifacts/lot-stories/group-deliveries', {
        query: {
          groupId
        }
      }),
    getGroupDelivery: (artifactId: string) =>
      request<LotStoryGroupDeliveryRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-delivery`),
    getGroupDeliveryReceipt: (artifactId: string, options?: LotStoryGroupDeliveryReceiptRequest) =>
      request<LotStoryGroupDeliveryReceiptRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-delivery-receipt`, {
        query: {
          historyCursor: options?.historyCursor,
          historyLimit: typeof options?.historyLimit === 'number' ? String(options.historyLimit) : undefined
        }
      }),
    getGroupReadPreflight: (artifactId: string, groupId?: string) =>
      request<LotStoryGroupReadPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-read-preflight`, {
        query: {
          groupId
        }
      }),
    getGroupShareReviewPreflight: (artifactId: string, groupId?: string) =>
      request<LotStoryGroupShareReviewPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-share-review-preflight`, {
        query: {
          groupId
        }
      }),
    recordGroupShareReview: (artifactId: string) =>
      request<LotStoryGroupShareReviewPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-share-review`, {
        method: 'POST'
      }),
    recordGroupShareApproval: (artifactId: string) =>
      request<LotStoryGroupShareReviewPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-share-approval`, {
        method: 'POST'
      }),
    recordGroupShareReleaseGate: (artifactId: string) =>
      request<LotStoryGroupShareReviewPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-share-release-gate`, {
        method: 'POST'
      }),
    promoteToGroup: (artifactId: string) =>
      request<LotStoryGroupShareReviewPreflightRecord>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/promote-to-group`, {
        method: 'POST'
      }),
    updateGroupShareIntent: (artifactId: string, payload: LotStoryGroupShareIntentUpdateRequest) =>
      request<LotStoryArtifactDetailV1>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/group-share-intent`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      }),
    preview: (payload: LotStoryPreviewRequest) =>
      request<LotStoryProjectionV1>('/community/artifacts/lot-stories/preview', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    createDraft: (payload: LotStoryDraftCreateRequest) =>
      request<LotStoryArtifactBundleV1>('/community/artifacts/lot-stories', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    publishDraft: (artifactId: string) =>
      request<LotStoryArtifactBundleV1>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/publish`, {
        method: 'POST'
      }),
    revokePublished: (artifactId: string) =>
      request<LotStoryArtifactBundleV1>(`/community/artifacts/lot-stories/${normalizePath(artifactId)}/revoke`, {
        method: 'POST'
      })
  },
  tasks: {
    list: (params?: { status?: 'open' | 'completed' | 'all'; assigneeId?: string; search?: string }) =>
      request<TaskRecord[]>('/tasks', {
        query: {
          status: params?.status,
          assigneeId: params?.assigneeId,
          search: params?.search
        }
      }),
    get: (id: string) => request<TaskRecord>(`/tasks/${normalizePath(id)}`),
    create: (payload: TaskCreatePayload) =>
      request<TaskRecord>('/tasks', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: TaskUpdatePayload) =>
      request<TaskRecord>(`/tasks/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/tasks/${normalizePath(id)}`, { method: 'DELETE' })
  },
  externalIdentifiers: {
    sources: {
      list: () => request<ExternalSource[]>('/external-identifiers/sources'),
      create: (payload: { key: string; name: string; description?: string; metadata?: Record<string, unknown> }) =>
        request<ExternalSource>('/external-identifiers/sources', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
    },
    links: {
      list: (filters?: ExternalSourceLinkFilters) =>
        request<ExternalSourceLink[]>('/external-identifiers/links', {
          query: {
            status: filters?.status,
            sourceKey: filters?.sourceKey,
            search: filters?.search
          }
        }),
      create: (payload: Record<string, unknown>) =>
        request<ExternalSourceLink>('/external-identifiers/links', {
          method: 'POST',
          body: JSON.stringify(payload)
        }),
      update: (id: string, payload: ExternalSourceLinkUpdatePayload) =>
        request<ExternalSourceLink>(`/external-identifiers/links/${normalizePath(id)}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        }),
      delete: (id: string) =>
        request<void>(`/external-identifiers/links/${normalizePath(id)}`, { method: 'DELETE' }),
      recordHit: (payload: Record<string, unknown>) =>
        request<ExternalSourceLink>('/external-identifiers/links/hit', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
    }
  },
  productionRecords: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lotId?: string
      lot?: string
      fromDate?: string
      toDate?: string
      noCache?: boolean
    }) =>
      request<ProductionRecord[]>('/production-records', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lotId: params?.lotId,
          lot: params?.lot,
          fromDate: params?.fromDate,
          toDate: params?.toDate
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<ProductionRecord>(`/production-records/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<ProductionRecord>('/production-records', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<ProductionRecord>(`/production-records/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/production-records/${normalizePath(id)}`, { method: 'DELETE' })
  },
  feedLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lotId?: string
      lot?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<FeedLog[]>('/feed-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lotId: params?.lotId,
          lot: params?.lot,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<FeedLog>(`/feed-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<FeedLog>('/feed-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<FeedLog>(`/feed-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    // Findings #66: an audited delete — the server requires a reason and
    // keeps the full prior row in CrewDataCorrection.
    delete: (id: string, audit: AuditedDeleteOptions) =>
      request<void>(`/feed-logs/${normalizePath(id)}`, { method: 'DELETE', body: JSON.stringify(audit) })
  },
  mortalityLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lot?: string
      lotId?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<MortalityLog[]>('/mortality-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lot: params?.lot,
          lotId: params?.lotId,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<MortalityLog>(`/mortality-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<MortalityLog>('/mortality-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<MortalityLog>(`/mortality-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    // Findings #66: an audited delete — the server requires a reason and
    // keeps the full prior row in CrewDataCorrection.
    delete: (id: string, audit: AuditedDeleteOptions) =>
      request<void>(`/mortality-logs/${normalizePath(id)}`, { method: 'DELETE', body: JSON.stringify(audit) })
  },
  weightSampleLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lot?: string
      lotId?: string
      productionRowId?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<WeightSampleLog[]>('/weight-sample-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lot: params?.lot,
          lotId: params?.lotId,
          productionRowId: params?.productionRowId,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<WeightSampleLog>(`/weight-sample-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<WeightSampleLog>('/weight-sample-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<WeightSampleLog>(`/weight-sample-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    // Findings #66: an audited delete — the server requires a reason and
    // keeps the full prior row in CrewDataCorrection.
    delete: (id: string, audit: AuditedDeleteOptions) =>
      request<void>(`/weight-sample-logs/${normalizePath(id)}`, { method: 'DELETE', body: JSON.stringify(audit) })
  },
  fishStockLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lot?: string
      lotId?: string
      productionRowId?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<FishStockLog[]>('/fish-stock-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lot: params?.lot,
          lotId: params?.lotId,
          productionRowId: params?.productionRowId,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<FishStockLog>(`/fish-stock-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<FishStockLog>('/fish-stock-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<FishStockLog>(`/fish-stock-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/fish-stock-logs/${normalizePath(id)}`, { method: 'DELETE' })
  },
  harvestLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lot?: string
      lotId?: string
      productionRowId?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<HarvestLog[]>('/harvest-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lot: params?.lot,
          lotId: params?.lotId,
          productionRowId: params?.productionRowId,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<HarvestLog>(`/harvest-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<HarvestLog>('/harvest-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<HarvestLog>(`/harvest-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/harvest-logs/${normalizePath(id)}`, { method: 'DELETE' })
  },
  // Tratamientos v0 (findings #103): online, office only.
  treatments: {
    list: (params: { farmId?: string; lotId?: string; includeDeleted?: boolean }) =>
      request<TreatmentRecord[]>('/treatments', {
        query: { farmId: params.farmId, lotId: params.lotId, includeDeleted: params.includeDeleted ? 'true' : undefined },
        cache: 'no-store'
      }),
    create: (payload: TreatmentCreatePayload) =>
      request<TreatmentRecord>('/treatments', { method: 'POST', body: JSON.stringify(payload) }),
    remove: (id: string, reason: string) =>
      request<TreatmentRecord>(`/treatments/${normalizePath(id)}`, { method: 'DELETE', body: JSON.stringify({ reason }) })
  },
  lotCostEvents: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lotId?: string
      categoryPrefix?: string
      costClassification?: string
      sourceEventType?: string
      sourceEventId?: string
      fromRecordedAt?: string
      toRecordedAt?: string
      noCache?: boolean
    }) =>
      request<LotCostEvent[]>('/lot-cost-events', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lotId: params?.lotId,
          categoryPrefix: params?.categoryPrefix,
          costClassification: params?.costClassification,
          sourceEventType: params?.sourceEventType,
          sourceEventId: params?.sourceEventId,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    get: (id: string) => request<LotCostEvent>(`/lot-cost-events/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<LotCostEvent>('/lot-cost-events', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<LotCostEvent>(`/lot-cost-events/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/lot-cost-events/${normalizePath(id)}`, { method: 'DELETE' })
  },
  fxRateLogs: {
    list: (params?: {
      baseCurrency?: string
      quoteCurrency?: string
      sourceId?: string
      sourceVersion?: string
      fromAsOf?: string
      toAsOf?: string
      noCache?: boolean
    }) =>
      request<FxRateLog[]>('/fx-rate-logs', {
        query: {
          baseCurrency: params?.baseCurrency,
          quoteCurrency: params?.quoteCurrency,
          sourceId: params?.sourceId,
          sourceVersion: params?.sourceVersion,
          fromAsOf: params?.fromAsOf,
          toAsOf: params?.toAsOf
        },
        cache: params?.noCache ? 'no-store' : undefined
      }),
    latest: (params: {
      baseCurrency: string
      quoteCurrency: string
      asOf: string
      sourceId?: string
      sourceVersion?: string
    }) =>
      request<FxRateLog>('/fx-rate-logs/latest', {
        query: {
          baseCurrency: params.baseCurrency,
          quoteCurrency: params.quoteCurrency,
          asOf: params.asOf,
          sourceId: params.sourceId,
          sourceVersion: params.sourceVersion
        }
      }),
    get: (id: string) => request<FxRateLog>(`/fx-rate-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<FxRateLog>('/fx-rate-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<FxRateLog>(`/fx-rate-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    delete: (id: string) => request<void>(`/fx-rate-logs/${normalizePath(id)}`, { method: 'DELETE' })
  },
  inventoryItems: {
    list: (params?: { farmId?: string; noCache?: boolean }) => request<InventoryItem[]>('/inventory-items', {
      query: {
        farmId: params?.farmId
      },
      cache: params?.noCache ? 'no-store' : undefined
    }),
    get: (id: string) => request<InventoryItem>(`/inventory-items/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) => request<InventoryItem>('/inventory-items', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
    update: (id: string, payload: Record<string, unknown>) => request<InventoryItem>(`/inventory-items/${normalizePath(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }),
    delete: (id: string) => request<void>(`/inventory-items/${normalizePath(id)}`, { method: 'DELETE' })
  },
  waterLogs: {
    list: (params?: {
      farmId?: string
      productionUnitId?: string
      lotId?: string
      lot?: string
      fromRecordedAt?: string
      toRecordedAt?: string
    }) =>
      request<WaterLog[]>('/water-logs', {
        query: {
          farmId: params?.farmId,
          productionUnitId: params?.productionUnitId,
          lotId: params?.lotId,
          lot: params?.lot,
          fromRecordedAt: params?.fromRecordedAt,
          toRecordedAt: params?.toRecordedAt
        }
      }),
    get: (id: string) => request<WaterLog>(`/water-logs/${normalizePath(id)}`),
    create: (payload: Record<string, unknown>) =>
      request<WaterLog>('/water-logs', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    update: (id: string, payload: Record<string, unknown>) =>
      request<WaterLog>(`/water-logs/${normalizePath(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      }),
    // Findings #66: an audited delete — the server requires a reason and
    // keeps the full prior row in CrewDataCorrection.
    delete: (id: string, audit: AuditedDeleteOptions) =>
      request<void>(`/water-logs/${normalizePath(id)}`, { method: 'DELETE', body: JSON.stringify(audit) })
  },
  sfrMatrix: {
    get: (species: string = 'global', options?: ListOptions) =>
      request<SfrMatrixRecord>(`/sfr-matrix/${normalizePath(species)}`, options?.noCache ? { cache: 'no-store' } : undefined),
    save: (payload: SfrMatrixSavePayload) => {
      const species = payload.species?.trim() || 'global'
      return request<SfrMatrixRecord>(`/sfr-matrix/${normalizePath(species)}`, {
        method: 'PUT',
        body: JSON.stringify({ species, matrix: payload.matrix })
      })
    }
  },
  mortalityMatrix: {
    get: (species: string = 'global', options?: ListOptions) =>
      request<MortalityMatrixRecord>(`/mortality-matrix/${normalizePath(species)}`, options?.noCache ? { cache: 'no-store' } : undefined),
    save: (payload: MortalityMatrixSavePayload) => {
      const species = payload.species?.trim() || 'global'
      return request<MortalityMatrixRecord>(`/mortality-matrix/${normalizePath(species)}`, {
        method: 'PUT',
        body: JSON.stringify({ species, matrix: payload.matrix })
      })
    }
  },
  // Crew Mode — Supervisor correction v0
  // (docs/crew-mode/prd/crew-mode-supervisor-correction-v0.md). Online-only
  // admin action; not routed through the entity outbox.
  crewCorrections: {
    list: (resource: string, entityId: string) =>
      request<CrewDataCorrectionRecord[]>('/crew-corrections', {
        query: { resource, entityId },
        cache: 'no-store'
      }),
    create: (payload: {
      resource: string
      entityId: string
      changes: Record<string, string | number>
      reason: string
      expectedUpdatedAt?: string
    }) =>
      request<{ correction: CrewDataCorrectionRecord; row: Record<string, unknown> }>('/crew-corrections', {
        method: 'POST',
        body: JSON.stringify(payload)
      }),
    // Findings #55: which rows were corrected (for the "Corregido" badge).
    summary: (resource: string, entityIds: string[]) =>
      request<CrewDataCorrectionSummaryRecord[]>('/crew-corrections/summary', {
        method: 'POST',
        body: JSON.stringify({ resource, entityIds })
      }),
    // Findings #54: move a row to another lot and/or production unit.
    move: (payload: {
      resource: string
      entityId: string
      lotId?: string
      productionUnitId: string
      reason: string
      expectedUpdatedAt: string
    }) =>
      request<{ correction: CrewDataCorrectionRecord; row: Record<string, unknown> }>('/crew-corrections/move', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
  },
  // Server-Side Flag Enforcement v0
  // (docs/crew-mode/prd/crew-mode-server-side-flag-enforcement-v0.md). The
  // admin-surface toggle (Charter O-4) for the FarmFeatureFlag table the four
  // capture services check. Online-only admin action; not outbox-routed.
  crewModeFlags: {
    list: (farmId: string) =>
      request<CrewModeFlagMap>('/crew-mode-flags', {
        query: { farmId },
        cache: 'no-store'
      }),
    set: (farmId: string, stream: CrewModeStreamId, enabled: boolean) =>
      request<CrewModeFlagMap>('/crew-mode-flags', {
        method: 'PUT',
        body: JSON.stringify({ farmId, stream, enabled })
      })
  }
}

export type CrewModeStreamId = 'water' | 'feed' | 'mortality' | 'weightSample'
export type CrewModeFlagMap = Record<CrewModeStreamId, boolean>

export type CrewDataCorrectionChangeRecord = {
  field: string
  from: string | number | null
  to: string | number | null
}

export type CrewDataCorrectionRecord = {
  id: string
  tenantKey: string
  resource: string
  entityId: string
  /** 'update' (values), 'delete' (audited delete, `snapshot` = prior row) or 'move'. */
  action?: 'update' | 'delete' | 'move'
  changes: CrewDataCorrectionChangeRecord[]
  snapshot?: Record<string, unknown>
  reason: string
  correctedByActorId: string
  correctedAt: string
}

export type LotStockIntoUnitsPayload = {
  farmId: string
  lotKey: string
  species?: string
  stockingDate: string
  notes?: string
  seedSupplier?: string
  strain?: string
  children: Array<{ productionUnitId: string; fishCount?: number }>
}

export type LotStockIntoUnitsResult = {
  parentLot: Lot
  childLots: Lot[]
  events: LotEvent[]
}

export type CrewDataCorrectionSummaryRecord = {
  entityId: string
  count: number
  lastCorrectedAt: string
}

export type AuditedDeleteOptions = {
  reason: string
  expectedUpdatedAt?: string
  /** Crew Mode undo: the PIN-identified operator removing their own entry. */
  operatorActorId?: string
}

/** findings #76: records a unit / facility holds (deleting is refused when > 0). */
export type RecordHoldings = {
  feedLogs: number
  mortalityLogs: number
  weightSampleLogs: number
  waterLogs: number
  fishStockLogs: number
  harvestLogs: number
  lotCostEvents: number
  productionRecords: number
  lots: number
  total: number
}

export type ProductionUnitAlias = {
  id: string
  aliasKey: string
  normalizedAliasKey: string
  displayLabel?: string
  canonicalFarmId?: string
  canonicalProductionUnitId?: string
  resolvedAt?: string
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export type ProductionUnitAliasRegisterPayload = {
  aliasKey: string
  displayLabel?: string
}

export type ProductionUnitAliasResolvePayload = {
  canonicalFarmId: string
  canonicalProductionUnitId: string
  displayLabel?: string
}

export type { TaskRecord } from '../types/task'
export type {
  ExternalSource,
  ExternalSourceLink,
  ExternalSourceLinkFilters,
  ExternalSourceLinkStatus,
  ExternalSourceLinkUpdatePayload
} from '../types/externalIdentifier'
export type { FeedLog, FishStockLog, LotCostEvent, MortalityLog, ProductionRecord, WaterLog, WeightSampleLog } from '../types/production'
export type { MortalityMatrixRecord } from '../types/mortalityMatrix'
export type { SfrMatrixRecord } from '../types/sfrMatrix'
